/**
 * LaunchDarklyContext - Session Replay & Feature Flags
 *
 * Integrates LaunchDarkly React SDK with session replay for observability.
 * Use this for dynamic feature flags and to track user sessions.
 */
import Observability from '@launchdarkly/observability';
import SessionReplay from '@launchdarkly/session-replay';
import {
  LDFlagSet,
  useFlags,
  useLDClient,
  withLDProvider,
} from 'launchdarkly-react-client-sdk';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { logWarn } from '@/lib/logger';

import { useAuth } from './AuthContext';

interface LaunchDarklyContextType {
  flags: LDFlagSet;
  ldClient: ReturnType<typeof useLDClient>;
  ready: boolean;
}

const LaunchDarklyContext = createContext<LaunchDarklyContextType | undefined>(
  undefined
);

/**
 * Inner component that provides LD context to children
 * This component is wrapped by withLDProvider HOC
 */
const LDContextBridge: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const flags = useFlags();
  const ldClient = useLDClient();
  const ready = ldClient?.waitUntilReady !== undefined;

  return (
    <LaunchDarklyContext.Provider value={{ flags, ldClient, ready }}>
      {children}
    </LaunchDarklyContext.Provider>
  );
};

/**
 * Hook to access LaunchDarkly client and flags
 * @example
 * const { flags, ldClient, ready } = useLaunchDarkly();
 */
export function useLaunchDarkly() {
  const context = useContext(LaunchDarklyContext);
  if (context === undefined) {
    throw new Error(
      'useLaunchDarkly must be used within a LaunchDarklyProvider'
    );
  }
  return context;
}

/**
 * Hook to get a specific LaunchDarkly flag with a default value
 * @example
 * const showFeature = useLDFlag('show-new-feature', false);
 * const modelLimit = useLDFlag('ai-model-limit', 5);
 */
export function useLDFlag<T>(flagKey: string, defaultValue: T): T {
  const { flags, ready } = useLaunchDarkly();

  // If LD not ready or flag doesn't exist, return default (fail closed)
  if (!ready || !(flagKey in flags)) {
    return defaultValue;
  }

  return flags[flagKey] as T;
}

/**
 * LaunchDarkly Provider Component with Session Replay
 *
 * Must be nested inside AuthProvider to access user context.
 * Automatically initializes session replay for observability.
 */
export function LaunchDarklyProvider({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_KEY;

  // Configure LD user context - memoize to maintain stable reference
  const userEmail = user?.email;
  const ldContext = useMemo(
    () => ({
      kind: 'user' as const,
      key: userEmail ?? 'anonymous',
      email: userEmail,
      anonymous: !userEmail,
    }),
    [userEmail]
  );

  // Create wrapped component with LD provider HOC including session replay plugin
  // This useMemo must be called unconditionally to follow Rules of Hooks
  const LDWrappedBridge = useMemo(() => {
    if (!clientSideID) {
      return null;
    }
    return withLDProvider({
      clientSideID,
      context: ldContext,
      options: {
        bootstrap: 'localStorage', // Cache flags in localStorage
        plugins: [
          new SessionReplay({
            enableCanvasRecording: true,
            samplingStrategy: {
              canvas: 2,
              canvasMaxSnapshotDimension: 1080,
              canvasFactor: 0.5,
            },
            recordCrossOriginIframe: true,
            privacySetting: 'none',
          }),
          new Observability({
            environment: import.meta.env.VITE_APP_ENV,
          }),
        ],
      },
    })(LDContextBridge);
  }, [clientSideID, ldContext]);

  // If no LD key configured, render children without LD (graceful degradation)
  if (!clientSideID) {
    logWarn(
      'VITE_LAUNCHDARKLY_CLIENT_KEY not configured. LaunchDarkly disabled.'
    );
    return (
      <LaunchDarklyContext.Provider
        value={{ flags: {}, ldClient: undefined, ready: false }}
      >
        {children}
      </LaunchDarklyContext.Provider>
    );
  }

  // Wait for auth to load before initializing LD
  if (loading) {
    return <>{children}</>;
  }

  // The HOC returns a component that accepts children
  return React.createElement(LDWrappedBridge!, null, children);
}
